import React from "react";
import { withRouter } from "react-router-dom";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import qs from "qs";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Slider from "@material-ui/core/Slider";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";

import Header from "../../components/utils/Header";
import Scenario from "../../api/Scenario";
import Version from "../../api/Version";
import {
  chainAsyncCalls,
  debounce,
  formatRatioPercent,
  formatFloat,
  formatRatio,
  formatUnit,
} from "../../utils";
import {
  AvailabilityStats,
  CapacityStats,
  ProdVsDemandStats,
  StockStats,
} from "./ScenarioComponents";
import { DotIcon, DownloadIcon } from "../../theme/AthenaIcons";
import ConditionalFormat from "../../components/utils/ConditionalFormat";
import WhiteTooltip from "../../components/utils/WhiteTooltip";
import ScenarioSelect from "./ScenarioSelect";
import BaseScenarioDashboardView from "../BaseScenarioDashboardView";

const styles = (theme) => ({
  tablePaper: {
    overflow: "auto",
  },
  noFiles: {
    height: 50,
  },
  thereAreFiles: {
    marginBottom: 18,
  },
  greyFont: {
    color: "#727272",
  },
  figure: {
    color: "#727272",
    fontWeight: 600,
  },
  softGreyFont: {
    color: "#b1b1b1",
  },
  simpleKPI: {
    padding: 25,
  },
  composedKPI: {
    padding: 25,
  },
  paperPadding: {
    padding: 25,
  },
  FontSemi: {
    fontWeight: 600,
  },
  blue: {
    color: theme.palette.primary.main,
  },
  FontBold: {
    fontWeight: 900,
    fontSize: 35,
  },
  alertValue: {
    paddingRight: theme.spacing(2),
  },
  separator: {
    borderColor: "#f2f3f7",
    height: 2,
    borderTop: "none",
    borderLeft: "none",
  },
  textSkeleton: {
    marginTop: 0,
    marginBottom: 0,
  },
  sliderBorder: {
    paddingTop: theme.spacing(2),
  },
});

@withStyles(styles)
@withRouter
@observer
class ScenarioComparisonView extends BaseScenarioDashboardView {
  static apiSubPath = "/scenario_comparison/";

  @observable scenario1 = null;
  @observable scenario2 = null;

  @observable availabilityThreshold = 0.95;
  @observable capacityMinThreshold = 0.95;
  @observable capacityMaxThreshold = 1.05;
  @observable prodVsDemandMinThreshold = 0.95;
  @observable prodVsDemandMaxThreshold = 1.05;

  @observable availabilityStats1 = null;
  @observable capacityStats1 = null;
  @observable stockStats1 = null;
  @observable prodVsDemand1 = null;
  @observable availabilityStats2 = null;
  @observable capacityStats2 = null;
  @observable stockStats2 = null;
  @observable prodVsDemand2 = null;

  _setData(data) {
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;

    if (this.scenario1 !== null && this.scenario2 !== null) {
      this.updateAvailabilityStats();
      this.updateCapacityStats();
      this.updateProdVsDemandStats();
      this.updateStockStats();
    }
  }

  get initialized() {
    return (
      super.initialized && this.instance1.isLoaded && this.instance2.isLoaded
    );
  }

  get instance1() {
    if (this.scenario1 === null) {
      return new Scenario({ id: null });
    }
    return Scenario.getForId(this.scenario1);
  }

  get instance2() {
    if (this.scenario2 === null) {
      return new Scenario({ id: null });
    }
    return Scenario.getForId(this.scenario2);
  }

  getDifference(kpiName) {
    if (!(this.instance1.isLoaded && this.instance2.isLoaded)) {
      const { classes } = this.props;
      return {
        id: null,
        name: kpiName,
        description: null,
        value: null,
        unit: null,
        ratio: null,
        skeleton: (
          <Skeleton
            variant="text"
            width={50}
            className={classes.textSkeleton}
          />
        ),
      };
    }
    const kpi1 = this.getKPIObject(this.instance1, kpiName);
    const kpi2 = this.getKPIObject(this.instance2, kpiName);
    const kpi_result = { ...kpi2 };
    kpi_result.value = kpi2.value - kpi1.value;
    kpi_result.unit = kpi1.unit === "%" ? "pp" : kpi1.unit;
    kpi_result.ratio = (kpi2.value - kpi1.value) / kpi1.value;
    return kpi_result;
  }

  @computed get availabilityStats3() {
    if (this.availabilityStats1 === null || this.availabilityStats2 === null) {
      return {
        nb_alerts: null,
        nb_alerts_ratio: null,
      };
    }
    return {
      nb_alerts:
        this.availabilityStats2.nb_alerts - this.availabilityStats1.nb_alerts,
      nb_alerts_ratio:
        this.availabilityStats2.nb_alerts_ratio -
        this.availabilityStats1.nb_alerts_ratio,
      vol_alerts_ratio:
        this.availabilityStats2.vol_alerts_ratio -
        this.availabilityStats1.vol_alerts_ratio,
    };
  }

  @computed get capacityStats3() {
    if (this.capacityStats1 === null || this.capacityStats2 === null) {
      return {
        nb_alerts: null,
        nb_alerts_ratio: null,
      };
    }
    return {
      nb_alerts: this.capacityStats2.nb_alerts - this.capacityStats1.nb_alerts,
      nb_alerts_ratio:
        this.capacityStats2.nb_alerts_ratio -
        this.capacityStats1.nb_alerts_ratio,
    };
  }

  @computed get prodVsDemandStats3() {
    if (this.prodVsDemand1 === null || this.prodVsDemand2 === null) {
      return {
        nb_alerts: null,
        nb_alerts_ratio: null,
      };
    }
    return {
      nb_alerts: this.prodVsDemand2.nb_alerts - this.prodVsDemand1.nb_alerts,
      nb_alerts_ratio:
        this.prodVsDemand2.nb_alerts_ratio - this.prodVsDemand1.nb_alerts_ratio,
    };
  }

  @computed get stockStats3() {
    if (this.stockStats1 === null || this.stockStats2 === null) {
      return {
        nb_alerts_below: null,
        nb_alerts_below_ratio: null,
        vol_alerts_below: null,
        vol_alerts_below_ratio: null,
        nb_alerts_above: null,
        nb_alerts_above_ratio: null,
        vol_alerts_above: null,
        vol_alerts_above_ratio: null,
      };
    }
    return {
      nb_alerts_below:
        this.stockStats2.nb_alerts_below - this.stockStats1.nb_alerts_below,
      nb_alerts_below_ratio:
        this.stockStats2.nb_alerts_below_ratio -
        this.stockStats1.nb_alerts_below_ratio,
      vol_alerts_below:
        this.stockStats2.vol_alerts_below - this.stockStats1.vol_alerts_below,
      vol_alerts_below_ratio:
        this.stockStats2.vol_alerts_below_ratio -
        this.stockStats1.vol_alerts_below_ratio,
      nb_alerts_above:
        this.stockStats2.nb_alerts_above - this.stockStats1.nb_alerts_above,
      nb_alerts_above_ratio:
        this.stockStats2.nb_alerts_above_ratio -
        this.stockStats1.nb_alerts_above_ratio,
      vol_alerts_above:
        this.stockStats2.vol_alerts_above - this.stockStats1.vol_alerts_above,
      vol_alerts_above_ratio:
        this.stockStats2.vol_alerts_above_ratio -
        this.stockStats1.vol_alerts_above_ratio,
    };
  }

  onScenario1Change = this.genericChangeHandler("scenario1");
  onScenario2Change = this.genericChangeHandler("scenario2");

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
    };
  }

  getAvailabilityPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold"] = this.availabilityThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/availability/?${qs.stringify(params)}`;
  }

  getCapacityPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold_min"] = this.capacityMinThreshold;
      params["threshold_max"] = this.capacityMaxThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/capacity/?${qs.stringify(params)}`;
  }

  getProdVsDemandPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold_min"] = this.prodVsDemandMinThreshold;
      params["threshold_max"] = this.prodVsDemandMaxThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/production_vs_demand/?${qs.stringify(params)}`;
  }

  getStockPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["alerts_only"] = "true";
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/stock/?${qs.stringify(params)}`;
  }

  updateAvailabilityStats = chainAsyncCalls(async () => {
    [this.availabilityStats1, this.availabilityStats2] = await Promise.all([
      this.instance1.ajaxRequestData("get", this.getAvailabilityPath(true)),
      this.instance2.ajaxRequestData("get", this.getAvailabilityPath(true)),
    ]);
  });

  debouncedUpdateAvailabilityStats = debounce(async () => {
    this.availabilityStats1 = null;
    this.availabilityStats2 = null;
    await this.updateAvailabilityStats();
  }, 1000);

  updateCapacityStats = chainAsyncCalls(async () => {
    [this.capacityStats1, this.capacityStats2] = await Promise.all([
      this.instance1.ajaxRequestData("get", this.getCapacityPath(true)),
      this.instance2.ajaxRequestData("get", this.getCapacityPath(true)),
    ]);
  });

  updateProdVsDemandStats = chainAsyncCalls(async () => {
    [this.prodVsDemand1, this.prodVsDemand2] = await Promise.all([
      this.instance1.ajaxRequestData("get", this.getProdVsDemandPath(true)),
      this.instance2.ajaxRequestData("get", this.getProdVsDemandPath(true)),
    ]);
  });

  debouncedUpdateCapacityStats = debounce(async () => {
    this.capacityStats1 = null;
    this.capacityStats2 = null;
    await this.updateCapacityStats();
  }, 1000);

  debouncedUpdateProdVsDemand = debounce(async () => {
    this.prodVsDemand1 = null;
    this.prodVsDemand2 = null;
    await this.updateProdVsDemandStats();
  }, 1000);

  updateStockStats = async () => {
    [this.stockStats1, this.stockStats2] = await Promise.all([
      this.instance1.ajaxRequestData("get", this.getStockPath()),
      this.instance2.ajaxRequestData("get", this.getStockPath()),
    ]);
  };

  onAvailabilityThresholdChange = (event, value) => {
    this.availabilityThreshold = value;
    this.debouncedUpdateAvailabilityStats();
  };

  onCapacityThresholdChange = (event, value) => {
    [this.capacityMinThreshold, this.capacityMaxThreshold] = value;
    this.debouncedUpdateCapacityStats();
  };

  onProdVsDemandThresholdChange = (event, value) => {
    [this.prodVsDemandMinThreshold, this.prodVsDemandMaxThreshold] = value;
    this.debouncedUpdateProdVsDemand();
  };

  listChanges() {
    const changedVersions = Object.entries(this.instance1.versions)
      .filter(
        ([versionType, versionId]) =>
          this.instance2.versions[versionType] !== versionId
      )
      .map(([versionType, versionId]) => versionId);
    const areDifferentVersionChanges = (versionChangesName) => {
      return (
        JSON.parse(this.instance1.version_changes)[versionChangesName] !==
        JSON.parse(this.instance2.version_changes)[versionChangesName]
      );
    };
    const areDifferentParameters = (parameters) => {
      return parameters.some(
        (parameter) => this.instance1[parameter] !== this.instance2[parameter]
      );
    };
    const changedParameters = Object.entries({
      "FG capacity [guided mode]": areDifferentVersionChanges(
        "fg_capacity_version"
      ),
      "SFG capacity [guided mode]": areDifferentVersionChanges(
        "sfg_capacity_version"
      ),
      "Demand forecasts [guided mode]": areDifferentVersionChanges(
        "demand_forecasts_version"
      ),
      "Stock target [guided mode]": areDifferentVersionChanges(
        "stock_target_version"
      ),
      "Risks & opportunities [guided mode]": areDifferentVersionChanges(
        "demand_risks_opps_version"
      ),
      "Raw & packs reverse BOM [guided mode]":
        areDifferentVersionChanges("rp_alerts_version"),
      "Demand changes on SFG": areDifferentVersionChanges(
        "sourcing_sfg_version"
      ),
      "Scenario duration": areDifferentParameters(["prediction_duration"]),
      "Mode B moves": areDifferentParameters([
        "optimization_constraints_min_month_move",
        "optimization_constraints_max_month_move",
        "optimization_constraints_techno_list_move",
        "optimization_constraints_plant_list_move",
        "optimization_moves_capacity",
        "optimization_move_capacity_step",
        "optimization_max_fg_transfers",
        "optimization_max_sfg_transfers",
      ]),
      "Objective function": areDifferentParameters([
        "optimization_below_min_alpha",
        "optimization_above_max_alpha",
        "optimization_below_target_alpha",
        "optimization_above_target_alpha",
        "optimization_margin_alpha",
        "optimization_constraints_lead_times",
        "optimization_add_innovations",
      ]),
    })
      .filter(([name, hasChanged]) => hasChanged)
      .map(([name, hasChanged]) => name);
    if (
      changedVersions.length > 0 ||
      Object.keys(changedParameters).length > 0
    ) {
      return (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {changedParameters.length} parameter
            {changedParameters.length > 1 ? "s" : ""}
            {" & "}
            {changedVersions.length} file{changedVersions.length > 1 ? "s" : ""}{" "}
            changed
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="column" spacing={2} wrap="nowrap">
              <Grid item>
                <ul>
                  {changedParameters.map((changedParameter) => (
                    <li key={changedParameter}>{changedParameter}</li>
                  ))}
                </ul>
              </Grid>
              {changedVersions.map((versionId) => {
                const version = Version.getForId(versionId);
                return (
                  <Grid item key={versionId}>
                    <Button
                      variant="contained"
                      color="primary"
                      href={version.file}
                      fullWidth
                    >
                      <Typography noWrap>{version.toString()}</Typography>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    }
    return "Same files & parameters";
  }

  getKPIObject(instanceObj, kpiName) {
    const kpiNames = kpiName.split(".");
    let kpiObject = instanceObj;
    for (let kpiObj of kpiNames) {
      kpiObject = kpiObject[kpiObj];
    }
    return kpiObject;
  }

  getKpiComparison(kpiName, big = false, reverseColors = false) {
    const { classes } = this.props;
    const diffKpi = this.getDifference(kpiName);
    const formatValue = (kpi) => {
      if ("skeleton" in diffKpi) {
        return diffKpi.skeleton;
      }
      if (kpi.unit === "%") {
        return formatRatioPercent(kpi.value);
      }
      if (kpi.unit === "pp") {
        return `${formatRatio(kpi.value)} pp`;
      }
      return `${formatFloat(kpi.value)} ${kpi.unit}`;
    };
    const conditionalFormatProps = { value: diffKpi.value };
    if (reverseColors !== null) {
      const isRatio = diffKpi.unit === "%" || diffKpi.unit === "pp";
      if (isRatio) {
        conditionalFormatProps.name = reverseColors
          ? "scenario-comparison-ratio-reverse"
          : "scenario-comparison-ratio";
      } else {
        conditionalFormatProps.name = reverseColors
          ? "scenario-comparison-reverse"
          : "scenario-comparison";
      }
    }

    return (
      <Grid container>
        <Grid
          item
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Grid item className={classes.greyFont}>
            {diffKpi.name}
          </Grid>
          {big ? (
            <Grid item>
              <Grid item container alignItems="center">
                {"skeleton" in diffKpi ? (
                  <Skeleton variant="text" width={200} />
                ) : (
                  <span
                    className={
                      classes[
                        `Font${
                          kpiName.indexOf("stock_") > -1 ? "Semi" : "Bold"
                        }`
                      ]
                    }
                  >
                    <ConditionalFormat {...conditionalFormatProps}>
                      {formatValue(diffKpi)} (
                      {formatRatioPercent(diffKpi.ratio)})
                    </ConditionalFormat>
                  </span>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid item>
              {"skeleton" in diffKpi ? (
                <Skeleton
                  variant="text"
                  width={100}
                  className={classes.textSkeleton}
                />
              ) : (
                <span className={classes.FontSemi}>
                  <ConditionalFormat {...conditionalFormatProps}>
                    {formatValue(diffKpi)}
                  </ConditionalFormat>
                </span>
              )}
            </Grid>
          )}
        </Grid>
        <Grid item container justify="space-between">
          <Grid item>
            <Grid container alignItems="center">
              <DotIcon fill="#8080d4" />
              <span className={classes.softGreyFont}>Scenario 1</span>
            </Grid>
          </Grid>
          <Grid item>
            <span className={classes.figure}>
              {formatValue(
                this.instance1.isLoaded
                  ? this.getKPIObject(this.instance1, kpiName)
                  : null
              )}
            </span>
          </Grid>
        </Grid>
        {this.instance2 === null ? null : (
          <Grid item container justify="space-between">
            <Grid item>
              <Grid container alignItems="center">
                <DotIcon fill="#e5b275" />
                <span className={classes.softGreyFont}>Scenario 2</span>
              </Grid>
            </Grid>
            <Grid item>
              <span className={classes.figure}>
                {formatValue(
                  this.instance2.isLoaded
                    ? this.getKPIObject(this.instance2, kpiName)
                    : null
                )}
              </span>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }

  moreInfo(text, num, color) {
    return (
      <WhiteTooltip title={`Scenario ${num}`} dotColor={color}>
        {text}
      </WhiteTooltip>
    );
  }

  createKpiComponent(
    title,
    kpiNames = ["kpi1", "kpi2"],
    reverseDefaultColors = false
  ) {
    const { classes } = this.props;
    return (
      <Paper className={classes.composedKPI}>
        <Grid
          item
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Grid item>
            <Typography
              variant="h6"
              className={[classes.FontSemi, classes.blue].join(" ")}
            >
              {title}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <hr className={classes.separator} />
        </Grid>
        <Grid item>
          {this.getKpiComparison(kpiNames[0], false, reverseDefaultColors)}
        </Grid>
        {kpiNames[1] === null ? null : (
          <>
            <Grid item>
              <hr className={classes.separator} />
            </Grid>
            <Grid item>
              {this.getKpiComparison(kpiNames[1], false, reverseDefaultColors)}
            </Grid>
          </>
        )}
      </Paper>
    );
  }

  lineKpiTitle(title, info) {
    const { classes } = this.props;
    return (
      <Grid
        item
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid item>
          <Typography
            variant="h6"
            className={[classes.FontSemi, classes.blue].join(" ")}
          >
            {title}
          </Typography>
        </Grid>
        <Grid item>{info}</Grid>
      </Grid>
    );
  }

  lineKpi(
    title,
    fill,
    stats,
    info,
    kpiName = "nb_alerts",
    kpiRatioName = "nb_alerts_ratio"
  ) {
    const { classes } = this.props;
    const unit = kpiName === "nb_alerts" ? null : "t";
    return (
      <Grid item container direction="row" justify="space-between">
        <Grid item>
          <Grid container alignItems="center">
            <DotIcon fill={fill} />
            <span className={classes.softGreyFont}>{title}</span>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container alignItems="center">
            <span className={classes.alertValue}>
              {!stats ? (
                <Skeleton
                  variant="text"
                  width={50}
                  className={classes.textSkeleton}
                />
              ) : (
                `${formatUnit(stats[kpiName], unit, 0)} (${formatUnit(
                  stats[kpiRatioName],
                  "%",
                  1,
                  true
                )})`
              )}
            </span>
            {info}
          </Grid>
        </Grid>
      </Grid>
    );
  }

  lineKpiTop(title, fill, value, info, kpiName, kpiRatioName) {
    const { classes } = this.props;
    const unit = kpiName === "nb_alerts" ? null : "t";
    return (
      <Grid item container direction="row" justify="space-between">
        <Grid item>
          <span className={classes.greyFont}>{title}</span>
        </Grid>
        <Grid item>
          <Grid container alignItems="center">
            <span className={classes.alertValue}>
              {!value ? (
                <Skeleton
                  variant="text"
                  width={50}
                  className={classes.textSkeleton}
                />
              ) : (
                <span className={classes.FontSemi}>
                  <ConditionalFormat
                    value={value[kpiName]}
                    name="scenario-comparison-ratio-reverse"
                  >
                    {formatUnit(value[kpiName], unit, 0)} (
                    {formatUnit(value[kpiRatioName], "pp", 1, true)})
                  </ConditionalFormat>
                </span>
              )}
            </span>
            {info}
          </Grid>
        </Grid>
      </Grid>
    );
  }

  scenarioDisplay() {
    if (!this.initialized) {
      return <Skeleton variant="rect" height={500} />;
    }
    const { classes } = this.props;
    return (
      <Grid container spacing={2}>
        <Grid item>
          <Typography variant="h6"> Simulation Settings </Typography>
        </Grid>
        <Grid item container spacing={2} alignItems="center">
          <Grid item xs={12} md={6} lg={4}>
            <ScenarioSelect
              label="Scenario 1"
              scenarioId={this.scenario1}
              choices={this.data.scenario1_choices}
              onScenarioChange={this.onScenario1Change}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <ScenarioSelect
              label="Scenario 2"
              scenarioId={this.scenario2}
              choices={this.data.scenario2_choices}
              onScenarioChange={this.onScenario2Change}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {this.listChanges()}
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6"> KPI overview </Typography>
        </Grid>
        <Grid item container direction="row" spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <Paper className={classes.simpleKPI}>
              {this.getKpiComparison("sales_kpi", true)}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper className={classes.simpleKPI}>
              {this.getKpiComparison("costs_kpi", true, true)}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper className={classes.simpleKPI}>
              {this.getKpiComparison("cash_locked_kpi", true, true)}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper className={classes.simpleKPI}>
              {this.getKpiComparison("prod_demand_kpi", true, true)}
            </Paper>
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={2} alignItems="stretch">
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            {this.createKpiComponent("Sales Coverage", [
              "availability_fg_kpi",
              null,
            ])}
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            {this.createKpiComponent(
              "Capacity utilization",
              ["capacity_fg_kpi", "capacity_sfg_kpi"],
              null
            )}
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            {this.createKpiComponent(
              "Stock projection",
              ["stock_fg_kpi", "stock_sfg_kpi"],
              null
            )}
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            {this.createKpiComponent(
              "Production Vs. Demand",
              ["fg_prod_demand_kpi", "sfg_prod_demand_kpi"],
              null
            )}
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6"> Alerts </Typography>
        </Grid>
        <Grid item container direction="row" spacing={2} alignItems="stretch">
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            <Paper className={classes.composedKPI}>
              {this.lineKpiTitle("Sales Coverage", "Products")}
              {this.lineKpiTop(
                "Scenario 2 - 1",
                "#000",
                this.availabilityStats3,
                this.moreInfo(
                  <AvailabilityStats
                    {...this.availabilityStats3}
                    threshold={this.availabilityThreshold}
                  />,
                  "2 - 1",
                  "#000"
                ),
                "nb_alerts",
                "nb_alerts_ratio"
              )}
              {this.lineKpi(
                "Scenario 1",
                "#8080d4",
                this.availabilityStats1,
                this.moreInfo(
                  <AvailabilityStats
                    {...this.availabilityStats1}
                    threshold={this.availabilityThreshold}
                  />,
                  1,
                  "#8080d4"
                )
              )}
              {this.lineKpi(
                "Scenario 2",
                "#e5b275",
                this.availabilityStats2,
                this.moreInfo(
                  <AvailabilityStats
                    {...this.availabilityStats2}
                    threshold={this.availabilityThreshold}
                  />,
                  2,
                  "#e5b275"
                )
              )}
              <Grid item container>
                <Grid
                  container
                  item
                  justify="space-between"
                  className={classes.sliderBorder}
                >
                  <Grid item>
                    <Typography
                      id="availability-threshold"
                      gutterBottom
                      className={classes.greyFont}
                    >
                      Alert threshold
                    </Typography>
                  </Grid>
                  <Grid item>
                    <span className={classes.greyFont}>
                      {formatRatioPercent(this.availabilityThreshold, 0)}
                    </span>
                  </Grid>
                </Grid>
                <Slider
                  value={this.availabilityThreshold}
                  onChange={this.onAvailabilityThresholdChange}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatRatioPercent}
                  aria-labelledby="availability-threshold"
                />
              </Grid>
            </Paper>
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            <Paper className={classes.composedKPI}>
              {this.lineKpiTitle("Capacity utilization", "Factory × Tech.")}
              {this.lineKpiTop(
                "Scenario 2 - 1",
                "#000",
                this.capacityStats3,
                this.moreInfo(
                  <CapacityStats
                    {...this.capacityStats3}
                    minThreshold={this.capacityMinThreshold}
                    maxThreshold={this.capacityMaxThreshold}
                  />,
                  "2 - 1",
                  "#000"
                ),
                "nb_alerts",
                "nb_alerts_ratio"
              )}
              {this.lineKpi(
                "Scenario 1",
                "#8080d4",
                this.capacityStats1,
                this.moreInfo(
                  <CapacityStats
                    {...this.capacityStats1}
                    minThreshold={this.capacityMinThreshold}
                    maxThreshold={this.capacityMaxThreshold}
                  />,
                  "1",
                  "#8080d4"
                )
              )}
              {this.lineKpi(
                "Scenario 2",
                "#e5b275",
                this.capacityStats2,
                this.moreInfo(
                  <CapacityStats
                    {...this.capacityStats2}
                    minThreshold={this.capacityMinThreshold}
                    maxThreshold={this.capacityMaxThreshold}
                  />,
                  "2",
                  "#e5b275"
                )
              )}
              <Grid item container>
                <Grid
                  container
                  item
                  justify="space-between"
                  className={classes.sliderBorder}
                >
                  <Grid item>
                    <Typography
                      id="availability-threshold"
                      gutterBottom
                      className={classes.greyFont}
                    >
                      Alert threshold
                    </Typography>
                  </Grid>
                  <Grid item>
                    <span className={classes.greyFont}>{`${formatRatioPercent(
                      this.capacityMinThreshold,
                      0
                    )} - ${formatRatioPercent(
                      this.capacityMaxThreshold,
                      0
                    )}`}</span>
                  </Grid>
                </Grid>
                <Slider
                  value={[this.capacityMinThreshold, this.capacityMaxThreshold]}
                  onChange={this.onCapacityThresholdChange}
                  max={2}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => formatRatioPercent(value, 0)}
                  track="inverted"
                  aria-labelledby="capacity-threshold"
                />
              </Grid>
            </Paper>
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            <Paper className={classes.composedKPI}>
              {this.lineKpiTitle("Stock Projection", "Product × Factory")}
              {this.lineKpiTop(
                "Avg Scenario 2 - 1 vol < min",
                "#000",
                this.stockStats3,
                this.moreInfo(
                  <StockStats {...this.stockStats3} showMin />,
                  "1",
                  "#000"
                ),
                "vol_alerts_below",
                "vol_alerts_below_ratio"
              )}
              {this.lineKpi(
                "Avg Scenario 1 vol < min",
                "#8080d4",
                this.stockStats1,
                this.moreInfo(
                  <StockStats {...this.stockStats1} showMin />,
                  "1",
                  "#8080d4"
                ),
                "vol_alerts_below",
                "vol_alerts_below_ratio"
              )}
              {this.lineKpi(
                "Avg Scenario 2 vol < min",
                "#e5b275",
                this.stockStats2,
                this.moreInfo(
                  <StockStats {...this.stockStats2} showMin />,
                  "2",
                  "#e5b275"
                ),
                "vol_alerts_below",
                "vol_alerts_below_ratio"
              )}
              <hr className={classes.separator} />
              {this.lineKpiTop(
                "Avg Scenario 2 - 1 vol > max",
                "#000",
                this.stockStats3,
                this.moreInfo(
                  <StockStats {...this.stockStats3} showMax />,
                  "1",
                  "#000"
                ),
                "vol_alerts_above",
                "vol_alerts_above_ratio"
              )}
              {this.lineKpi(
                "Avg Scenario 1 vol > max",
                "#8080d4",
                this.stockStats1,
                this.moreInfo(
                  <StockStats {...this.stockStats1} showMax />,
                  "1",
                  "#8080d4"
                ),
                "vol_alerts_above",
                "vol_alerts_above_ratio"
              )}
              {this.lineKpi(
                "Avg Scenario 2 vol > max",
                "#e5b275",
                this.stockStats2,
                this.moreInfo(
                  <StockStats {...this.stockStats2} showMax />,
                  "2",
                  "#e5b275"
                ),
                "vol_alerts_above",
                "vol_alerts_above_ratio"
              )}
            </Paper>
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={3}>
            <Paper className={classes.composedKPI}>
              {this.lineKpiTitle("Production Vs. Demand", "Factory × Tech.")}
              {this.lineKpiTop(
                "Scenario 2 - 1",
                "#000",
                this.prodVsDemandStats3,
                this.moreInfo(
                  <ProdVsDemandStats
                    {...this.prodVsDemandStats3}
                    minThreshold={this.prodVsDemandMinThreshold}
                    maxThreshold={this.prodVsDemandMaxThreshold}
                  />,
                  "2 - 1",
                  "#000"
                ),
                "nb_alerts",
                "nb_alerts_ratio"
              )}
              {this.lineKpi(
                "Scenario 1",
                "#8080d4",
                this.prodVsDemand1,
                this.moreInfo(
                  <ProdVsDemandStats
                    {...this.prodVsDemand1}
                    minThreshold={this.prodVsDemandMinThreshold}
                    maxThreshold={this.prodVsDemandMaxThreshold}
                  />,
                  "1",
                  "#8080d4"
                )
              )}
              {this.lineKpi(
                "Scenario 2",
                "#e5b275",
                this.prodVsDemand2,
                this.moreInfo(
                  <ProdVsDemandStats
                    {...this.prodVsDemand2}
                    minThreshold={this.prodVsDemandMinThreshold}
                    maxThreshold={this.prodVsDemandMaxThreshold}
                  />,
                  "2",
                  "#e5b275"
                )
              )}
              <Grid item container>
                <Grid
                  container
                  item
                  justify="space-between"
                  className={classes.sliderBorder}
                >
                  <Grid item>
                    <Typography
                      id="availability-threshold"
                      gutterBottom
                      className={classes.greyFont}
                    >
                      Alert threshold
                    </Typography>
                  </Grid>
                  <Grid item>
                    <span className={classes.greyFont}>{`${formatRatioPercent(
                      this.prodVsDemandMinThreshold,
                      0
                    )} - ${formatRatioPercent(
                      this.prodVsDemandMaxThreshold,
                      0
                    )}`}</span>
                  </Grid>
                </Grid>
                <Slider
                  value={[
                    this.prodVsDemandMinThreshold,
                    this.prodVsDemandMaxThreshold,
                  ]}
                  onChange={this.onProdVsDemandThresholdChange}
                  max={2}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => formatRatioPercent(value, 0)}
                  track="inverted"
                  aria-labelledby="prod-vs-demand-threshold"
                />
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6"> Required actions </Typography>
        </Grid>
        <Grid item container direction="row" spacing={2}>
          <Grid item container direction="column" xs={12} md={6} lg={4}>
            <Paper className={classes.paperPadding}>
              <Grid container>
                <Grid item>
                  <Grid container alignItems="center">
                    <DotIcon fill="#8080d4" />
                    <span className={classes.softGreyFont}>Scenario 1</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                {this.instance1.required_actions.length === 0
                  ? "No required actions for Scenario 1"
                  : this.instance1.required_actions.map((action) => (
                      <p key={action}>{action}</p>
                    ))}
              </Grid>
            </Paper>
          </Grid>
          <Grid item container direction="column" xs={12} md={6} lg={4}>
            <Paper className={classes.paperPadding}>
              <Grid container>
                <Grid item>
                  <Grid container alignItems="center">
                    <DotIcon fill="#e5b275" />
                    <span className={classes.softGreyFont}>Scenario 2</span>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                {this.instance2.required_actions.length === 0
                  ? "No required actions for Scenario 2"
                  : this.instance2.required_actions.map((action) => (
                      <p key={action}>{action}</p>
                    ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Scenario Comparison" />
          </Grid>
          <Grid item>
            <Tooltip title="Download scenarios (coming soon)">
              <IconButton
                href={Scenario.getApiUrl(
                  "/scenario_comparison/?" +
                    this.getQueryString({ export: "xlsx" })
                )}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid
          item
          container
          spacing={2}
          justify="space-between"
          alignItems="flex-start"
        >
          <Grid item xs>
            {this.instance1 === null || this.instance2 === null ? (
              <Skeleton variant="rect" width="100%" height={500} />
            ) : (
              this.scenarioDisplay()
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default ScenarioComparisonView;
